(() => {
  if (window.OcWishlistTracking || !window.OcShopify?.trackItemPayload) return;
  window.OcWishlistTracking = true;
  const PLACEHOLDER = '.js_wishlist-tracking';
  const PENDING_SELECTION = 'oc:wishlist-selection:v1';
  const cache = new Map();
  const queue = [];
  let active = 0;
  let navigation = 0;

  function context(node) {
    const { productHandle, productId, variantId, index } = node.dataset;
    const url = new URL(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(productHandle)}`, location.origin);
    const variant = Number(variantId) > 0 ? variantId : '';
    if (variant) url.searchParams.set('variant', variant);
    url.searchParams.set('section_id', 'on-add-product-info');
    const preview = new URLSearchParams(location.search).get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);

    const fallback = node.dataset.itemName && node.dataset.price !== '' && Number.isFinite(Number(node.dataset.price))
      ? { ...node.dataset }
      : null;
    return { productHandle, productId, variantId: variant, index: Number(index), url: url.href, fallback };
  }

  function pump() {
    while (active < 4 && queue.length) {
      active++;
      queue.shift()().finally(() => { active--; pump(); });
    }
  }

  function load(info) {
    const key = `${info.productId}|${info.url}|${window.Shopify?.currency?.active || ''}`;
    const existing = cache.get(key);
    if (existing && (!existing.failedAt || Date.now() - existing.failedAt < 30000)) return existing;
    const entry = {};
    cache.set(key, entry);
    entry.promise = new Promise(resolve => {
      queue.push(async () => {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 4000);
        try {
          const response = await fetch(info.url, { signal: controller.signal });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const data = doc.querySelector('.js_fetched-product-info')?.dataset;
          // Handles can change; the saved product and variant IDs establish identity.
          if (!data?.price || data.productId !== info.productId ||
              (info.variantId && data.variantId !== info.variantId)) {
            throw new Error('Missing or mismatched wishlist variant');
          }
          entry.data = { ...data };
          resolve(entry.data);
        } catch (error) {
          entry.failedAt = Date.now();
          console.warn('[Wishlist tracking] Product data unavailable:', info.productHandle, error.message);
          resolve(info.fallback || null);
        } finally {
          clearTimeout(timer);
        }
      });
      pump();
    });
    return entry;
  }

  function hydrate(node) {
    if (node.__wishlistLoading) return;
    node.__wishlistLoading = true;
    const info = context(node);
    load(info).promise.then(data => {
      node.__wishlistLoading = false;
      if (!data || !node.isConnected) return;
      // Prefer Shopify's full payload, retaining the saved row when it is unavailable.
      const tracking = document.createElement('span');
      tracking.style.display = 'none';
      tracking.className = 'js_gtm-track';
      Object.assign(tracking.dataset, data, { index: String(info.index), itemListName: 'wishlist', quantity: '1' });
      const legacy = node.closest('.wrp_wish_product')?.querySelector('.action_wishlist');
      if (legacy) Object.assign(legacy.dataset, {
        id: data.itemSku, name: data.itemName, variant: data.itemVariant,
        price: String(window.OcShopify.formatMoney(data.price)), category: data.itemCategory || '',
      });
      node.replaceWith(tracking);
    });
  }

  function readPending() {
    try { return JSON.parse(sessionStorage.getItem(PENDING_SELECTION) || 'null'); } catch (error) { return null; }
  }

  function clearPending() {
    try { sessionStorage.removeItem(PENDING_SELECTION); } catch (error) {}
  }

  function select(data, info) {
    window.OcItemLists.remember(data, 'wishlist');
    const item = window.OcShopify.trackItemPayload(data);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ event: 'select_item', ecommerce: {
      items: [{ ...item, item_list_name: 'wishlist', index: info.index }],
    } });
  }

  function trackPendingSelection(event) {
    if (event.type === 'auxclick' && event.button !== 1) return;
    const link = event.target.closest?.('a[href]');
    if (!link || !window.OcItemLists.productHandle(link.href)) return;
    const node = link.closest('.wrp_wish_product')?.querySelector(PLACEHOLDER);
    if (!node) return; // Hydrated cards use the theme's regular click handler.
    const info = context(node);
    window.OcItemLists.remember(info, 'wishlist');
    const pending = { ...info, at: Date.now() };
    const sameTab = event.type !== 'auxclick' && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey &&
      (!link.target || link.target === '_self') && new URL(link.href).origin === location.origin;
    if (sameTab) {
      try { sessionStorage.setItem(PENDING_SELECTION, JSON.stringify(pending)); } catch (error) {}
    }
    const currentNavigation = navigation;
    load(info).promise.then(data => {
      if (!data || currentNavigation !== navigation) return;
      if (sameTab && readPending()?.at === pending.at) clearPending();
      select(data, info);
    });
  }

  function init() {
    const pending = readPending();
    if (pending) {
      clearPending();
      const data = document.querySelector('product-form .js_gtm-track')?.dataset;
      if (Date.now() - pending.at < 30000 && data?.productHandle === pending.productHandle &&
          (!pending.variantId || data.variantId === pending.variantId)) {
        // The PDP can display a product-level "from" price. Restore the saved
        // wishlist variant's price from its section response instead.
        load(pending).promise.then(data => { if (data) select(data, pending); });
      }
    }
    const grid = document.getElementById('wishlisthtml');
    if (!grid) return;
    const scan = () => grid.querySelectorAll(PLACEHOLDER).forEach(hydrate);
    new MutationObserver(scan).observe(grid, { childList: true, subtree: true });
    document.addEventListener('visibilitychange', () => { if (!document.hidden) scan(); });
    document.addEventListener('scroll', scan, { passive: true });
    scan();
  }

  document.addEventListener('click', trackPendingSelection, true);
  document.addEventListener('auxclick', trackPendingSelection, true);
  window.addEventListener('pagehide', () => { navigation++; });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
